import {Component, Input, Output, EventEmitter, OnInit} from '@angular/core';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {EventResponse, TrainingPackageResponse, TrainingRegistrationResponse} from '../../../../../../../../@shared/models/response.module';
import {TrainingService} from '../../../../../../../../@shared/services/training.service';
import {ToastService} from '../../../../../../../../@core/services/toast.service';
import {ViewPackageRegistrations} from './components/view-package-registrations/view-package-registrations';
import {AddPackage} from './components/add-package/add-package';
import {EditPackage} from './components/edit-package/edit-package';
import {MembershipPricingModal} from '../manage-event-classes/components/membership-pricing-modal/membership-pricing-modal';
import {TranslateService} from '@ngx-translate/core';
import {DeleteModal} from '../../../../../../../../@shared/components/delete-modal/delete-modal';

@Component({
  selector: 'app-manage-training-packages',
  standalone: false,
  templateUrl: './manage-training-packages.html',
  styleUrl: './manage-training-packages.css'
})
export class ManageTrainingPackages implements OnInit {
  @Input() event!: EventResponse;
  @Output() dataChanged = new EventEmitter<void>();

  packages: TrainingPackageResponse[] = [];
  registrations: TrainingRegistrationResponse[] = [];
  loading = false;
  loadingRegistrations = false;

  constructor(
    private trainingService: TrainingService,
    private modalService: NgbModal,
    private toastService: ToastService,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    this.loadPackages();
    this.loadRegistrations();
  }

  loadPackages() {
    if (!this.event?.event_id) return;

    this.loading = true;
    this.trainingService.getPackages({ event_id: this.event.event_id }).subscribe({
      next: (packages) => {
        this.packages = packages || [];
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.toastService.error(this.translateService.instant('manageEvent.trainingPackages.loadError'));
      }
    });
  }

  loadRegistrations() {
    if (!this.event?.event_id) return;

    this.loadingRegistrations = true;
    this.trainingService.getRegistrations({ event_id: this.event.event_id }).subscribe({
      next: (registrations) => {
        this.registrations = registrations || [];
        this.loadingRegistrations = false;
      },
      error: () => {
        this.loadingRegistrations = false;
      }
    });
  }

  getPackageRegistrations(packageId: number): TrainingRegistrationResponse[] {
    return this.registrations.filter(r => r.package_id === packageId);
  }

  getPackageRegistrationCount(packageId: number): number {
    return this.getPackageRegistrations(packageId).length;
  }

  getPaidRegistrationCount(packageId: number): number {
    return this.getPackageRegistrations(packageId).filter(r => r.payment_status === 'paid').length;
  }

  getPendingRegistrationCount(packageId: number): number {
    // Count pay_later registrations (not yet paid but confirmed)
    // Note: 'pending' status is now filtered out server-side (abandoned checkouts)
    return this.getPackageRegistrations(packageId).filter(r => r.payment_status === 'pay_later').length;
  }

  getPackageRevenue(pkg: TrainingPackageResponse): number {
    const paidCount = this.getPaidRegistrationCount(pkg.package_id);
    return (pkg.price_cents / 100) * paidCount;
  }

  /**
   * Calculate net amount after platform + payment fees
   * Stripe: 2.4% + 2.00 NOK, Platform: 2%
   * Net = price × 0.956 - 2.00 per transaction
   */
  calculateNetAmount(grossAmount: number, transactionCount: number): number {
    if (!grossAmount || grossAmount <= 0) return 0;
    const net = grossAmount * 0.956 - (2 * transactionCount);
    return Math.max(0, Math.round(net * 100) / 100);
  }

  getPackageNetRevenue(pkg: TrainingPackageResponse): number {
    const paidCount = this.getPaidRegistrationCount(pkg.package_id);
    const grossRevenue = this.getPackageRevenue(pkg);
    return this.calculateNetAmount(grossRevenue, paidCount);
  }

  formatPrice(priceCents: number, currency: string): string {
    return `${(priceCents / 100).toFixed(2)} ${currency}`;
  }

  getSessionTypeLabel(type: string): string {
    switch (type) {
      case 'group': return this.translateService.instant('manageEvent.trainingPackages.typeGroup');
      case 'individual': return this.translateService.instant('manageEvent.trainingPackages.typeIndividual');
      case 'both': return this.translateService.instant('manageEvent.trainingPackages.typeBoth');
      default: return type;
    }
  }

  openViewRegistrationsModal(pkg: TrainingPackageResponse) {
    const modalRef = this.modalService.open(ViewPackageRegistrations, {
      size: 'xl',
      centered: true
    });
    modalRef.componentInstance.package = pkg;
    modalRef.componentInstance.registrations = this.getPackageRegistrations(pkg.package_id);
    modalRef.componentInstance.event = this.event;

    modalRef.result.then((result) => {
      if (result) {
        this.loadRegistrations();
      }
    }).catch(() => {});
  }

  openAddPackageModal() {
    const modalRef = this.modalService.open(AddPackage, {
      size: 'lg',
      centered: true
    });
    modalRef.componentInstance.event = this.event;

    modalRef.result.then((result) => {
      if (result) {
        this.loadPackages();
        this.dataChanged.emit();
      }
    }).catch(() => {});
  }

  openEditPackageModal(pkg: TrainingPackageResponse) {
    const modalRef = this.modalService.open(EditPackage, {
      size: 'lg',
      centered: true
    });
    modalRef.componentInstance.package = pkg;
    modalRef.componentInstance.event = this.event;

    modalRef.result.then((result) => {
      if (result) {
        this.loadPackages();
        this.dataChanged.emit();
      }
    }).catch(() => {});
  }

  deletePackage(pkg: TrainingPackageResponse) {
    const modalRef = this.modalService.open(DeleteModal, { centered: true });
    modalRef.componentInstance.description = this.translateService.instant('manageEvent.trainingPackages.deleteConfirm', { name: pkg.package_name });

    modalRef.result.then((confirmed) => {
      if (confirmed) {
        this.trainingService.deletePackage(pkg.package_id).subscribe({
          next: () => {
            this.toastService.success(this.translateService.instant('manageEvent.trainingPackages.deleteSuccess'));
            this.loadPackages();
            this.loadRegistrations();
            this.dataChanged.emit();
          },
          error: (error) => {
            console.error('Error deleting package:', error);
            this.toastService.error(this.translateService.instant('manageEvent.trainingPackages.deleteError'));
          }
        });
      }
    }).catch(() => {});
  }

  openMembershipPricingModal(pkg: TrainingPackageResponse) {
    const modalRef = this.modalService.open(MembershipPricingModal, { size: 'lg', centered: true });
    modalRef.componentInstance.itemType = 'package';
    modalRef.componentInstance.itemId = pkg.package_id;
    modalRef.componentInstance.itemName = pkg.package_name;
    modalRef.componentInstance.basePrice = pkg.price_cents || 0;
    modalRef.componentInstance.useCents = true;
    modalRef.componentInstance.currency = this.event?.default_currency || pkg.currency || 'NOK';

    modalRef.result.then((result) => {
      if (result) {
        this.loadPackages();
      }
    }).catch(() => {});
  }

  get totalRegistrations(): number {
    return this.registrations.length;
  }

  get totalPaidRegistrations(): number {
    return this.registrations.filter(r => r.payment_status === 'paid').length;
  }

  get totalRevenue(): number {
    let total = 0;
    for (const pkg of this.packages) {
      total += this.getPackageNetRevenue(pkg);
    }
    return total;
  }

  get currency(): string {
    return this.event?.default_currency || this.packages[0]?.currency || 'NOK';
  }
}
